import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useModal } from 'mui-modal-provider';

import OfferSuccessDialog from '../../components/Dialogs/OfferSuccess/OfferSuccessDialog';
import { BackdropLoading } from '../../contexts/Backgrop/Backdrop';
import { OfferCategory } from '../../graphql/API';
import { useVenueGetShopifyStoreProductQuery } from '../../hooks/reactQuery/shopify/useVenueGetShopifyStoreProductQuery';
import { useCreateOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import useQueryParams from '../../hooks/useQueryParams';
import { useAppSelector } from '../../redux/hooks';
import { makeShopifyProductGid } from '../../utils/shopify';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import OfferForm from './ProductForm';
import { FormValue, initialValues, mapToCreateOfferInput } from './components/schema';

const CreateProduct: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { productId } = useParams() || {};
  const { getQueryParam } = useQueryParams();
  const offerType = getQueryParam('type');
  const venueState = useAppSelector((state) => state.venue.value);

  const isShopifyProduct = location.pathname.includes('shopify-products');

  const completeProductId = productId ? makeShopifyProductGid(productId) : '';

  const { data: shopifyProduct } = useVenueGetShopifyStoreProductQuery({
    productID: completeProductId,
  });

  const { showModal } = useModal();

  React.useEffect(() => {
    if (!venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online)) {
      navigate('/offers/create');
    }
  }, [navigate, venueState]);

  React.useEffect(() => {
    window.localStorage.setItem('product-form-step', JSON.stringify(0));
  }, []);

  const productInfo = React.useMemo(() => {
    const productInfo = JSON.parse(shopifyProduct?.productJSON || '{}');
    const { title, featuredMedia, description } = productInfo;
    const picture = featuredMedia?.preview?.image?.url;
    const offerValue = productInfo?.priceRangeV2?.maxVariantPrice?.amount;
    return { title, picture, description, offerValue };
  }, [shopifyProduct]);

  const values: FormValue = {
    ...initialValues,
    shopifyProductID: completeProductId,
    ...(offerType === 'collab' ? { category: OfferCategory.unknown, type: 'private' } : {}),
  };

  const mutation = useCreateOfferQuery('/products?status=pending', () => {
    window.localStorage.setItem('product-form-data', JSON.stringify(values));
    window.localStorage.setItem('product-form-step', JSON.stringify(0));
  });

  return (
    <>
      <OfferForm
        method='insert'
        formConfig={{
          initialValues: {
            ...initialValues,
            ...(offerType === 'collab' ? { category: OfferCategory.unknown, type: 'private' } : {}),
            ...(isShopifyProduct && productInfo && shopifyProduct
              ? {
                  title: productInfo.title,
                  description: productInfo.description,
                  offerValue: productInfo.offerValue | 0,
                  offerImageKey: productInfo.picture,
                  shopifyProductID: completeProductId,
                }
              : {}),
          },
          onSubmit: async (values: FormValue) => {
            if (venueState) {
              const input = mapToCreateOfferInput(
                venueState.id,
                values,
                venueState?.timeZoneId || 'America/Los_Angeles',
              );

              await mutation.mutateAsync(input);

              showModal(OfferSuccessDialog, {});
            }
          },
        }}
        isShopifyProduct={isShopifyProduct}
        offerTypeIndex={offerType === 'collab' ? 1 : -1}
      />
      {mutation.isPending && <BackdropLoading open />}
    </>
  );
};

export default CreateProduct;
